import { useState, useEffect, useCallback } from 'react';
import { useAuthStore, useWorkspaceStore, useDocumentStore, useUIStore } from '@/stores';
import { AuthForm } from '@/components/auth/AuthForm';
import { Sidebar } from '@/components/layout/Sidebar';
import { DocumentView } from '@/components/documents/DocumentView';
import { CommandPalette } from '@/components/shared/CommandPalette';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api-client';

export default function Home() {
  const { user, token, isAuthenticated, setUser } = useAuthStore();
  const { setWorkspaces, setCurrentWorkspace, workspaces } = useWorkspaceStore();
  const { setDocuments, documents } = useDocumentStore();
  const { sidebarOpen, commandPaletteOpen, setCommandPaletteOpen } = useUIStore();
  const [isLoading, setIsLoading] = useState(true);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const data = await api.get<{ success: boolean; user: any }>('/auth/me');

        if (data.success && data.user) {
          setUser(data.user);
        }
      } catch (error) {
        console.error('Auth check error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (mounted) {
      checkAuth();
    }
  }, [mounted, setUser]);

  const loadWorkspaces = useCallback(async () => {
    try {
      const data = await api.get<{ success: boolean; workspaces: any[] }>('/workspaces');

      if (data.success && data.workspaces) {
        setWorkspaces(data.workspaces);
        
        const defaultWorkspace = data.workspaces.find((w: { isDefault: boolean }) => w.isDefault) || data.workspaces[0];
        if (defaultWorkspace) {
          setCurrentWorkspace(defaultWorkspace);
        }
      }
    } catch (error) {
      console.error('Error loading workspaces:', error);
    }
  }, [setWorkspaces, setCurrentWorkspace]);

  const loadDocuments = useCallback(async (workspaceId: string) => {
    try {
      const data = await api.get<{ success: boolean; documents: any[] }>(`/documents?workspaceId=${workspaceId}`);

      if (data.success && data.documents) {
        setDocuments(data.documents);
      }
    } catch (error) {
      console.error('Error loading documents:', error);
    }
  }, [setDocuments]);

  useEffect(() => {
    if (isAuthenticated && workspaces.length === 0) {
      loadWorkspaces();
    }
  }, [isAuthenticated, workspaces.length, loadWorkspaces]);

  useEffect(() => {
    const currentWorkspace = useWorkspaceStore.getState().currentWorkspace;
    if (currentWorkspace) {
      loadDocuments(currentWorkspace.id);
    }
  }, [workspaces, loadDocuments]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(true);
      }

      if ((e.metaKey || e.ctrlKey) && e.key === 'n') {
        e.preventDefault();
        const ws = useWorkspaceStore.getState().currentWorkspace;
        if (ws) {
          api.post('/documents', { title: 'Nuevo documento', workspaceId: ws.id })
            .then((data) => {
              if (data.success && data.document) {
                useDocumentStore.getState().addDocument(data.document);
                useDocumentStore.getState().setCurrentDocument(data.document);
                toast.success('Documento creado');
              }
            })
            .catch((err) => {
              console.error('Error creating document:', err);
            });
        }
      }

      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        toast.success('Guardado automáticamente');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setCommandPaletteOpen]);

  if (!mounted || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AuthForm onSuccess={loadWorkspaces} />;
  }

  return (
    <div className="h-screen flex overflow-hidden bg-background">
      {sidebarOpen && <Sidebar />}

      <main className="flex-1 flex flex-col overflow-hidden">
        <DocumentView />
      </main>

      <CommandPalette
        open={commandPaletteOpen}
        onOpenChange={setCommandPaletteOpen}
      />
    </div>
  );
}
